import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { api, setToken, formatApiError } from "../lib/api";
import { getPushPlatform, requestFCMToken } from "../lib/firebase";
import { isNativePlatform } from "../lib/capacitor-push";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

/**
 * Silently refresh FCM token only when the browser has ALREADY granted
 * notification permission. Never triggers the permission popup — that is
 * handled by <NotificationPrompt />.
 */
async function tryRegisterFCMToken() {
  try {
    // Native Capacitor permission is handled by the Push Notifications plugin.
    // The browser-only checks below would incorrectly skip Android registration.
    if (!isNativePlatform()) {
      if (!("Notification" in window)) return;
      if (Notification.permission !== "granted") return; // NotificationPrompt handles the rest
    }
    const token = await requestFCMToken();
    if (token) {
      await api.post("/notifications/register-device", { token, platform: getPushPlatform() });
    }
  } catch {
    // Non-critical — never block login
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);   // null = checking, false = guest, {} = user
  const [loading, setLoading] = useState(true);
  const authRequestId = useRef(0);

  const refresh = useCallback(async () => {
    const requestId = ++authRequestId.current;
    try {
      const { data } = await api.get("/auth/me");
      const authenticatedUser = data?.user || data;
      if (!authenticatedUser || typeof authenticatedUser !== "object") {
        throw new Error("Invalid authentication response");
      }
      if (requestId === authRequestId.current) {
        setUser(authenticatedUser);
        // Re-register an existing session's device token after app startup.
        tryRegisterFCMToken();
      }
    } catch (err) {
      // A rejected persisted token must not leave protected pages in a blank state.
      if (requestId !== authRequestId.current) return;
      if ([401, 403].includes(err?.response?.status)) setToken(null);
      setUser(false);
    } finally {
      if (requestId === authRequestId.current) setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const requestId = ++authRequestId.current;
    const { data } = await api.post("/auth/login", { email, password });
    const payload = data?.data || data;
    if (!payload?.token || !payload?.user) {
      const error = new Error("Login response did not include a token and user");
      error.response = { data };
      throw error;
    }
    setToken(payload.token);
    setUser(payload.user);
    // Register FCM token after login (best-effort, non-blocking)
    tryRegisterFCMToken();
    return payload.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    const response = data?.data || data;
    if (!response?.token || !response?.user) {
      const error = new Error("Registration response did not include a token and user");
      error.response = { data };
      throw error;
    }
    setToken(response.token);
    setUser(response.user);
    tryRegisterFCMToken();
    return response.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setToken(null);
    setUser(false);
  };

  const updateProfile = async (payload) => {
    const { data } = await api.put("/users/me", payload);
    setUser(data);
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateProfile, refresh, formatApiError }}>
      {children}
    </AuthContext.Provider>
  );
}
